import { safeGet, safeSet } from '@mononoke/shared';
import { localStore } from './browser.js';

/**
 * Light/dark theme — the redesign's token layer needs exactly one switch.
 *
 * THE PREFERENCE AND THE RESOLVED MODE ARE SEPARATE THINGS (the intent-vs-value
 * standing rule, applied to appearance): the stored preference is `system | light |
 * dark`, and what the CSS sees is always a RESOLVED `data-theme="light|dark"` on the
 * root element. Resolving in JS instead of layering `@media` and `[data-theme]`
 * overrides in CSS keeps every token defined in exactly two blocks — one per mode —
 * with no third "system" copy to drift out of sync.
 *
 * Persistence goes through `browser.ts`'s probed handle like the language setting
 * one file over: where storage is unavailable this degrades to "follow the OS each
 * boot", which is the correct behaviour, not an error.
 */

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'mononoke.theme';

/** Keep in sync with `--surface` in tokens.json — the PWA title bar sits over the top
 * nav band, and the band is drawn on `--surface` (DESIGN.md component contract). The
 * old values (#f7f5f2 / #191512) matched no token exactly; since 0.1.264 the pair IS
 * the token value, so "in sync" is checkable rather than approximate. */
const THEME_COLOR = { light: '#ece6da', dark: '#1c1815' } as const;

function isPreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

let preference: ThemePreference = 'system';
const listeners = new Set<(preference: ThemePreference) => void>();

/** jsdom (unit tests) has no matchMedia; treat that as "light unless told otherwise". */
const media =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function resolve(): 'light' | 'dark' {
  if (preference !== 'system') return preference;
  return media?.matches === true ? 'dark' : 'light';
}

function apply(): void {
  const mode = resolve();
  document.documentElement.dataset['theme'] = mode;
  // The installed PWA's title bar keeps matching the app instead of staying stuck
  // on the color index.html shipped with.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[mode]);
}

export function themePreference(): ThemePreference {
  return preference;
}

export function setThemePreference(next: ThemePreference): void {
  preference = next;
  if (localStore) safeSet(localStore, STORAGE_KEY, next);
  apply();
  for (const listener of listeners) listener(next);
}

/** Subscribe to preference changes; fires immediately, like subscribeRoute. */
export function subscribeTheme(listener: (preference: ThemePreference) => void): () => void {
  listeners.add(listener);
  listener(preference);
  return () => listeners.delete(listener);
}

/** Call once at boot, before the first render — a flash of the wrong theme is the
 * appearance version of the flash-of-English initLanguage prevents. */
export function initTheme(): void {
  const stored = localStore ? safeGet(localStore, STORAGE_KEY) : null;
  if (isPreference(stored)) preference = stored;
  // While the preference is "system", follow the OS live — someone whose phone
  // switches to dark at sunset should not need to reload the app.
  media?.addEventListener('change', () => {
    if (preference === 'system') apply();
  });
  apply();
}
